import { useEffect, useState } from 'react';

const UPDATE_CHECK_INTERVAL_MS = 60000;
const APK_UPDATE_CHECK_INTERVAL_MS = 5 * 60000;
const APK_UPDATE_REMIND_MS = 4 * 60 * 60000;
const RELOAD_SIGNATURE_KEY = 'biztrack-last-reload-signature';
const APK_DISMISS_KEY_PREFIX = 'biztrack-apk-update-dismissed-until';

const getManifestSignature = (manifest) => {
  const files = manifest?.files || {};
  const entrypoints = Array.isArray(manifest?.entrypoints) ? manifest.entrypoints.join('|') : '';
  return [
    files['main.js'],
    files['main.css'],
    files['index.html'],
    entrypoints
  ].filter(Boolean).join('|');
};

const getAndroidBridge = () => {
  if (typeof window === 'undefined') return null;
  return window.BizTrackAndroid || null;
};

const getBridgeValue = (methodNames) => {
  const bridge = getAndroidBridge();
  if (!bridge) return '';

  for (const methodName of methodNames) {
    if (typeof bridge[methodName] !== 'function') continue;
    try {
      const value = bridge[methodName]();
      if (value !== undefined && value !== null && value !== '') return value;
    } catch (err) {
      console.log('Android bridge read skipped:', err.message);
    }
  }

  return '';
};

const isAndroidWebView = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android/i.test(ua) && (/\bwv\b/i.test(ua) || /Version\/\d+\.\d+/i.test(ua));
};

const getInstalledAndroidInfo = () => {
  const bridge = getAndroidBridge();
  const packageName = String(getBridgeValue(['getAppPackageName', 'getPackageName']) || '').trim();
  const versionCode = Number(getBridgeValue(['getVersionCode']) || 0);

  if (bridge || isAndroidWebView()) {
    return {
      isInstalledApp: true,
      packageName,
      versionCode: Number.isInteger(versionCode) && versionCode > 0 ? versionCode : 0
    };
  }

  return { isInstalledApp: false, packageName: '', versionCode: 0 };
};

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null') || {};
  } catch {
    return {};
  }
};

const getAdminIdForUpdateCheck = () => {
  const params = new URLSearchParams(window.location.search || '');
  const queryAdminId = Number(params.get('wl') || params.get('admin_id') || 0);
  if (queryAdminId > 0) return queryAdminId;

  const user = readStoredUser();
  const scopedId = Number(user.admin_id || 0) || (['admin', 'whitelabel_admin'].includes(user.role) ? Number(user.id || 0) : 0);
  return scopedId > 0 ? scopedId : 0;
};

const getApkDismissKey = (update) => [
  APK_DISMISS_KEY_PREFIX,
  update?.package_name || 'app',
  update?.latest_version_code || 'latest'
].join(':');

const shouldSkipDismissedApkUpdate = (update) => {
  const until = Number(localStorage.getItem(getApkDismissKey(update)) || 0);
  return until > Date.now();
};

export default function LiveAppUpdater() {
  const [apkUpdate, setApkUpdate] = useState(null);

  useEffect(() => {
    let active = true;
    let currentSignature = '';

    const checkForUpdate = async () => {
      try {
        const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
          }
        });
        if (!res.ok || !active) return;

        const signature = getManifestSignature(await res.json());
        if (!signature) return;
        if (!currentSignature) {
          currentSignature = signature;
          return;
        }

        if (signature !== currentSignature) {
          if (sessionStorage.getItem(RELOAD_SIGNATURE_KEY) === signature) {
            currentSignature = signature;
            return;
          }
          sessionStorage.setItem(RELOAD_SIGNATURE_KEY, signature);
          window.location.reload();
        }
      } catch (err) {
        console.log('Live app update check skipped:', err.message);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };

    checkForUpdate();
    window.__biztrackCheckForUpdate = checkForUpdate;
    const interval = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    window.addEventListener('focus', checkForUpdate);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      active = false;
      window.clearInterval(interval);
      if (window.__biztrackCheckForUpdate === checkForUpdate) delete window.__biztrackCheckForUpdate;
      window.removeEventListener('focus', checkForUpdate);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const checkApkUpdate = async () => {
      const installed = getInstalledAndroidInfo();
      if (!installed.isInstalledApp) return;

      try {
        const params = new URLSearchParams({
          host: window.location.host,
          version_code: String(installed.versionCode || 0),
          ts: String(Date.now())
        });
        if (installed.packageName) params.set('package_name', installed.packageName);
        const adminId = getAdminIdForUpdateCheck();
        if (adminId > 0) params.set('admin_id', String(adminId));

        const res = await fetch(`/api/workspace/apps/update-check?${params.toString()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
          }
        });
        if (!res.ok || !active) return;

        const data = await res.json();
        const nextUpdate = data?.update_available && data?.app?.download_url
          ? {
              ...data.app,
              latest_version_code: data.latest_version_code,
              latest_version_name: data.latest_version_name,
              current_version_code: data.current_version_code
            }
          : null;

        if (nextUpdate && shouldSkipDismissedApkUpdate(nextUpdate)) return;
        setApkUpdate(nextUpdate);
      } catch (err) {
        console.log('APK update check skipped:', err.message);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkApkUpdate();
    };

    checkApkUpdate();
    const interval = window.setInterval(checkApkUpdate, APK_UPDATE_CHECK_INTERVAL_MS);
    window.addEventListener('focus', checkApkUpdate);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', checkApkUpdate);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const openApkUpdate = () => {
    const url = apkUpdate?.download_url || apkUpdate?.direct_download_url;
    if (!url) return;

    const bridge = getAndroidBridge();
    if (bridge && typeof bridge.openExternal === 'function') {
      try {
        bridge.openExternal(url);
        return;
      } catch (err) {
        console.log('Android external update open skipped:', err.message);
      }
    }

    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (isAndroidWebView()) {
      window.setTimeout(() => {
        try {
          window.location.assign(url);
        } catch (err) {
          console.log('APK update navigation skipped:', err.message);
        }
      }, 250);
    }
  };

  const dismissApkUpdate = () => {
    if (apkUpdate) {
      localStorage.setItem(getApkDismissKey(apkUpdate), String(Date.now() + APK_UPDATE_REMIND_MS));
    }
    setApkUpdate(null);
  };

  if (!apkUpdate) return null;

  return (
    <div style={{
      position: 'fixed',
      left: 16,
      right: 16,
      bottom: 18,
      zIndex: 9999,
      display: 'flex',
      justifyContent: 'center',
      pointerEvents: 'none'
    }}>
      <div role="dialog" aria-live="polite" style={{
        width: 'min(520px, 100%)',
        border: '1px solid rgba(14, 116, 144, 0.24)',
        borderRadius: 8,
        background: 'linear-gradient(135deg, #ffffff 0%, #f0fdfa 52%, #eff6ff 100%)',
        boxShadow: '0 22px 60px rgba(15, 23, 42, 0.22)',
        color: '#0f172a',
        padding: 14,
        pointerEvents: 'auto',
        fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
      }}>
        <strong style={{ display: 'block', fontSize: 15, lineHeight: 1.25 }}>Mobile app update available</strong>
        <small style={{ display: 'block', marginTop: 5, color: '#475569', lineHeight: 1.45 }}>
          {apkUpdate.app_name || 'Android app'} v{apkUpdate.latest_version_code || 'latest'} is ready.
        </small>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <button type="button" onClick={openApkUpdate} style={{
            border: 0,
            borderRadius: 8,
            background: '#0f766e',
            color: '#ffffff',
            cursor: 'pointer',
            fontWeight: 800,
            minHeight: 42,
            padding: '0 12px'
          }}>
            Update now
          </button>
          <button type="button" onClick={dismissApkUpdate} style={{
            border: '1px solid rgba(100, 116, 139, 0.28)',
            borderRadius: 8,
            background: '#ffffff',
            color: '#334155',
            cursor: 'pointer',
            fontWeight: 800,
            minHeight: 42,
            padding: '0 12px'
          }}>
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
